/**
 * Settings -> Integrations: connecting Google Workspace / Microsoft 365 so interview scheduling
 * can create a real calendar event with a real Google Meet / Teams link, instead of a pasted one.
 *
 * THE OAUTH DANCE, IN THREE ROUTES, mirroring exactly what a browser redirect flow needs:
 *   1. `getConnectUrl` — the web app calls this first; the response's `authUrl` is where the
 *      browser navigates NEXT (a plain `window.location.href = authUrl`, not through this typed
 *      client — an OAuth consent screen is a full page navigation, not an XHR).
 *   2. Google/Microsoft redirects back to a WEB app route (not this API) with `?code=&state=` in
 *      the query string — same shape as an email-verification or password-reset link landing on a
 *      web route that then calls a contract endpoint, not a raw server-side redirect handler.
 *   3. `completeConnect` — the web route reads `code`/`state` off its own URL and POSTs them here;
 *      the server exchanges the code for tokens with the provider directly (server-to-server, the
 *      client never sees a token) and persists the CalendarIntegration row.
 *
 * `state` is a short-lived, signed opaque value (see apps/api's implementation) carrying enough to
 * verify the callback belongs to the org/actor that started it — never trust `state` as anything
 * but a CSRF/replay guard, never decode it client-side.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  calendarProviderSchema,
  errorBodySchema,
  instantSchema,
  integrationStatusSchema,
  orgIdSchema,
  uuidSchema,
  zohoRegionSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const calendarIntegrationSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  provider: calendarProviderSchema,
  connectedEmail: z.string(),
  connectedByPersonId: uuidSchema,
  connectedByName: z.string(),
  status: integrationStatusSchema,
  scope: z.string(),
  tokenExpiresAt: instantSchema,
  createdAt: instantSchema,
});
export type CalendarIntegration = z.infer<typeof calendarIntegrationSchema>;

export const connectUrlResponseSchema = z.object({
  authUrl: z.string().url(),
});

export const completeConnectBodySchema = z.object({
  code: z.string().min(1),
  state: z.string().min(1),
});

export const slackInstallationSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  teamId: z.string(),
  teamName: z.string().nullable(),
  installedByPersonId: uuidSchema.nullable(),
  isActive: z.boolean(),
  createdAt: instantSchema,
  updatedAt: instantSchema,
});
export type SlackInstallation = z.infer<typeof slackInstallationSchema>;

export const slackStatusSchema = z.object({
  isConnected: z.boolean(),
  teamId: z.string().optional(),
  teamName: z.string().nullable().optional(),
  installedAt: instantSchema.optional(),
});
export type SlackStatus = z.infer<typeof slackStatusSchema>;

/**
 * Zoho Meeting — used by Training to create live sessions and read their participant reports back.
 * Its own routes (like Slack's) rather than a third `:provider` value: Zoho's redirect carries an
 * extra `accounts-server` query param naming the data centre the account lives in, and the code
 * must be exchanged at THAT host. The server only ever posts to an allow-listed Zoho accounts host
 * — `accountsServer` is a hint used to pick one, never a URL fetched as given.
 */
export const completeZohoConnectBodySchema = completeConnectBodySchema.extend({
  accountsServer: z.string().url().max(200).optional(),
});

export const zohoMeetingStatusSchema = z.object({
  isConnected: z.boolean(),
  status: integrationStatusSchema.nullable(),
  connectedEmail: z.string().nullable(),
  connectedByName: z.string().nullable(),
  region: zohoRegionSchema.nullable(),
  connectedAt: instantSchema.nullable(),
  /** Set when a token refresh or an API call failed and the connection needs re-authorising. Never
   *  carries a token or a raw provider response body. */
  lastErrorMessage: z.string().nullable(),
});
export type ZohoMeetingStatus = z.infer<typeof zohoMeetingStatusSchema>;

export const integrationsContract = c.router(
  {
    list: {
      method: 'GET',
      path: '/',
      responses: { 200: z.array(calendarIntegrationSchema), 401: errorBodySchema },
      summary: "The org's connected calendars (at most one per provider)",
      metadata: routeMeta(null),
    },
    getSlackConnectUrl: {
      method: 'GET',
      path: '/slack/connect-url',
      responses: {
        200: connectUrlResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Get OAuth consent URL for Slack integration',
      metadata: routeMeta('integration:manage'),
    },
    completeSlackConnect: {
      method: 'POST',
      path: '/slack/complete',
      body: completeConnectBodySchema,
      responses: {
        200: slackInstallationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Exchange Slack OAuth code for bot token and persist installation',
      metadata: routeMeta('integration:manage'),
    },
    getSlackStatus: {
      method: 'GET',
      path: '/slack/status',
      responses: {
        200: slackStatusSchema,
        401: errorBodySchema,
      },
      summary: 'Check whether Slack integration is active for the current org',
      metadata: routeMeta(null),
    },
    disconnectSlack: {
      method: 'POST',
      path: '/slack/disconnect',
      body: c.noBody(),
      responses: {
        200: z.object({ success: z.boolean() }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Disconnect and revoke Slack integration for the current org',
      metadata: routeMeta('integration:manage'),
    },
    getConnectUrl: {
      method: 'GET',
      path: '/:provider/connect-url',
      responses: {
        200: connectUrlResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Where to send the browser to start the OAuth consent flow',
      metadata: routeMeta('integration:manage'),
    },
    completeConnect: {
      method: 'POST',
      path: '/:provider/complete',
      body: completeConnectBodySchema,
      responses: {
        200: calendarIntegrationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Exchange the OAuth code for tokens and persist the connection',
      metadata: routeMeta('integration:manage'),
    },
    disconnect: {
      method: 'POST',
      path: '/:provider/disconnect',
      body: c.noBody(),
      responses: {
        200: calendarIntegrationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Revoke and remove a connected calendar',
      metadata: routeMeta('integration:manage'),
    },
  },
  { pathPrefix: '/integrations' },
);

/**
 * A separate router (not more keys on `integrationsContract`) so it can land contract-first without
 * a handler. MOUNT IT BEFORE `integrationsContract`: that router's `/:provider/connect-url` would
 * otherwise capture `/zoho/connect-url` and reject `zoho` as an unknown calendar provider.
 */
export const zohoIntegrationContract = c.router(
  {
    getConnectUrl: {
      method: 'GET',
      path: '/connect-url',
      responses: {
        200: connectUrlResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Where to send the browser to start the Zoho Meeting OAuth consent flow',
      metadata: routeMeta('integration:manage'),
    },
    completeConnect: {
      method: 'POST',
      path: '/complete',
      body: completeZohoConnectBodySchema,
      responses: {
        200: zohoMeetingStatusSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Exchange the Zoho OAuth code for tokens and persist the connection',
      metadata: routeMeta('integration:manage'),
    },
    getStatus: {
      method: 'GET',
      path: '/status',
      responses: { 200: zohoMeetingStatusSchema, 401: errorBodySchema },
      summary: 'Whether Zoho Meeting is connected for the current org',
      metadata: routeMeta(null),
    },
    disconnect: {
      method: 'POST',
      path: '/disconnect',
      body: c.noBody(),
      responses: {
        200: zohoMeetingStatusSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Revoke and remove the Zoho Meeting connection',
      metadata: routeMeta('integration:manage'),
    },
  },
  { pathPrefix: '/integrations/zoho' },
);
